/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ISearchBoxProps } from '@fluentui/react/lib/SearchBox';
import { Stack } from '@fluentui/react/lib/Stack';
import { Observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { WithDebouncedInputProps } from 'src/core/utils/hocs/withDebouncedInput';
import { SearchStrategy } from 'src/core/utils/searchStrategies';
import { DomainStore } from 'src/luis/types/stores/domains/DomainStore';
import { DomainCard } from 'src/luis/verticals/domainsList/DomainCard';

export type DomainListProps = {
    /**
     * The domain and state of the cards.
     */
    domains: DomainStore[];

    /**
     * Boolean to determines whether the domains are loaded or not.
     */
    isLoading: boolean;

    /**
     * The current application culture.
     */
    culture: string;

    /**
     * A click callback function to handle click
     * events on the card.
     */
    onCardClicked: (domainName: string) => void;

    /**
     * User role to extract actions available for user.
     */
    userRole: string;

    /**
     * User role to show in luis tool tip.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'domains.domainList.searchPlaceholder',
        defaultMessage: 'Search for a domain ...'
    },
    emptyPlaceholder: {
        id: 'domains.domainList.emptyPlaceholder',
        defaultMessage: 'No domains supported for "{culture}" culture'
    },
    noResults: {
        id: 'domains.domainList.noResults',
        defaultMessage: 'No results found'
    }
});

const { styleComponent } = getStylist('DomainsList');

const SearchBox = styleComponent<ISearchBoxProps & WithDebouncedInputProps>(LuisDebouncedSearchBox)('SearchBox', theme => ({
    width: theme.searchBoxWidth,
    marginBottom: theme.searchBoxBottomMargin
}));

/**
 * Represents prebuilt domains list.
 *
 * Note: The SearchInput is surrounded by Stack.Item deliberately
 * to avoid a quirk with Fabric that causes the search box to collapse
 * when there are no domains.
 */
export const DomainsList = (props: DomainListProps) => {
    const { domains, isLoading, onCardClicked, culture, userRole, displayedUserRole } = props;
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');

    const { canWrite } = mapUserRoleToUserActions(userRole);

    /**
     * Filters domains by query, sorts and renders them. If
     * no domains match the query, a placeholder message is displayed.
     */
    const renderCards = () => {
        const cards = SearchStrategy.substringSearchStrategy(domains, query, 'name')
            .sort((l: DomainStore, r: DomainStore) => (l.name.toLocaleLowerCase() > r.name.toLocaleLowerCase() ? 1 : -1))
            .map(domain => (
                <Observer key={domain.name}>
                    {() => (
                        <DomainCard
                            name={domain.name}
                            description={domain.description}
                            state={domain.state}
                            onCardClicked={() => onCardClicked(domain.name)}
                            isAddorRemoveDisabled={!canWrite}
                            displayedUserRole={displayedUserRole}
                        />
                    )}
                </Observer>
            ));

        const noDomains = (
            <LuisText data-automation-id="emptyMsgWrapper" variant="large">
                {query ? localize(messages.noResults) : localize(messages.emptyPlaceholder, { culture })}
            </LuisText>
        );

        return (
            <>
                {cards.length ? cards : noDomains}
                {query.length ? <SearchResultsAnnouncer itemsLength={cards.length} /> : null}
            </>
        );
    };

    return (
        <Stack tokens={{ childrenGap: 30 }}>
            {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}
            <Stack.Item>
                <SearchBox
                    ariaLabel={localize(messages.searchPlaceholder)}
                    size={24}
                    placeholder={localize(messages.searchPlaceholder)}
                    value={query}
                    onChange={(_, newValue?: string) => setQuery(newValue)}
                />
            </Stack.Item>

            <Stack horizontal disableShrink horizontalAlign="start" wrap verticalFill tokens={{ childrenGap: 40 }}>
                {isLoading ? <ShimmerFactory shimmerCount={10} /> : renderCards()}
            </Stack>
        </Stack>
    );
};
